import { useState, useEffect } from 'react';
import { Link2, Plus, Trash2, Edit2, ExternalLink, Image, Upload, X, Globe, Phone, MessageCircle, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MeusLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    icon_url: '',
    icon_type: 'url',
    master_phone: '',
    credit_cost: ''
  });
  const [iconPreview, setIconPreview] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user-links-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
      }
    } catch (error) {
      console.error('Erro ao carregar links:', error);
      toast.error('Erro ao carregar links');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (link = null) => {
    if (link) {
      setEditingLink(link);
      setFormData({
        name: link.name,
        url: link.url,
        icon_url: link.icon_url || '',
        icon_type: link.icon_type || 'url',
        master_phone: link.master_phone || '',
        credit_cost: link.credit_cost || ''
      });
      setIconPreview(link.icon_url || '');
    } else {
      setEditingLink(null);
      setFormData({ name: '', url: '', icon_url: '', icon_type: 'url', master_phone: '', credit_cost: '' });
      setIconPreview('');
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingLink(null);
    setFormData({ name: '', url: '', icon_url: '', icon_type: 'url', master_phone: '', credit_cost: '' });
    setIconPreview('');
  };

  const openWhatsApp = (phone) => {
    if (!phone) {
      toast.error('Número não cadastrado');
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const phoneWithCountry = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
    window.open(`https://wa.me/${phoneWithCountry}`, '_blank');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Imagem muito grande. Máximo 2MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData({ ...formData, icon_url: base64, icon_type: 'upload' });
        setIconPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUrlChange = (url) => {
    setFormData({ ...formData, icon_url: url, icon_type: 'url' });
    setIconPreview(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.url.trim()) {
      toast.error('Nome e URL são obrigatórios');
      return;
    }

    // Validar URL
    try {
      new URL(formData.url);
    } catch {
      toast.error('URL inválida. Use o formato: https://exemplo.com');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const method = editingLink ? 'PUT' : 'POST';
      const url = editingLink 
        ? `${API_URL}/api/user-links/${editingLink.id}`
        : `${API_URL}/api/user-links`;

      // Preparar dados com credit_cost como número
      const dataToSend = {
        ...formData,
        credit_cost: formData.credit_cost ? parseFloat(formData.credit_cost) : null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        toast.success(editingLink ? 'Link atualizado!' : 'Link adicionado!');
        handleCloseDialog();
        fetchLinks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar link');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao salvar link');
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Tem certeza que deseja excluir este link?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user-links/${linkId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Link excluído!');
        fetchLinks();
      } else {
        toast.error('Erro ao excluir link');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao excluir link');
    }
  };

  const openLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="meus-links-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Link2 className="w-8 h-8 text-emerald-500" />
              Meus Links
            </h1>
            <p className="text-slate-400 mt-1">Salve os links dos seus servidores com contato do master</p>
          </div>
          
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="add-link-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Link
          </Button>
        </div>

        {/* Lista de Links */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : links.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
              <Link2 className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Nenhum link salvo</h3>
            <p className="text-slate-400 mb-4">Adicione seus links de servidores para acesso rápido</p>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Primeiro Link
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {links.map((link) => (
              <div 
                key={link.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-emerald-500/30 transition-all group"
                data-testid={`link-card-${link.id}`}
              >
                {/* Ícone e Nome */}
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden cursor-pointer hover:ring-2 hover:ring-emerald-500/50 transition-all"
                    onClick={() => openLink(link.url)}
                  >
                    {link.icon_url ? (
                      <img 
                        src={link.icon_url} 
                        alt={link.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <Globe 
                      className="w-6 h-6 text-zinc-500" 
                      style={{ display: link.icon_url ? 'none' : 'block' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{link.name}</h3>
                    {link.master_phone && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" />
                        {link.master_phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-3 gap-2 mb-3 p-2 bg-zinc-800/50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Custo/Créd</p>
                    <p className="text-sm font-semibold text-emerald-400">
                      {link.credit_cost ? `R$ ${link.credit_cost.toFixed(2)}` : '-'}
                    </p>
                  </div>
                  <div className="text-center border-x border-zinc-700">
                    <p className="text-xs text-slate-500">Clientes</p>
                    <p className="text-sm font-semibold text-violet-400">
                      {link.clients_count || 0}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="text-sm font-semibold text-sky-400">
                      R$ {(link.total_value || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openLink(link.url)}
                    className="flex-1 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-600/30"
                    size="sm"
                    data-testid={`open-link-${link.id}`}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Abrir
                  </Button>
                  {link.master_phone && (
                    <Button
                      onClick={() => openWhatsApp(link.master_phone)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`whatsapp-link-${link.id}`}
                      title="Chamar Master no WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    onClick={() => handleOpenDialog(link)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white hover:bg-zinc-800"
                    data-testid={`edit-link-${link.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(link.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-link-${link.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dialog de Adicionar/Editar */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-emerald-500" />
                {editingLink ? 'Editar Link' : 'Novo Link'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nome */}
              <div>
                <Label htmlFor="name" className="text-slate-300">Nome do Servidor</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Servidor Principal"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="link-name-input"
                />
              </div>

              {/* URL */}
              <div>
                <Label htmlFor="url" className="text-slate-300">URL do Link</Label>
                <Input
                  id="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://exemplo.com"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="link-url-input"
                />
              </div>

              {/* Ícone */}
              <div>
                <Label className="text-slate-300">Ícone (opcional)</Label>
                
                {/* Preview do Ícone */}
                {iconPreview && (
                  <div className="mt-2 mb-3 flex items-center gap-3">
                    <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden border border-zinc-700">
                      <img 
                        src={iconPreview} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={() => setIconPreview('')}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIconPreview('');
                        setFormData({ ...formData, icon_url: '', icon_type: 'url' });
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remover
                    </Button>
                  </div>
                )}

                {/* Tabs para escolher tipo de ícone */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {/* Upload de Imagem */}
                  <label className="cursor-pointer">
                    <div className="flex items-center justify-center gap-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-emerald-500/50 transition-colors">
                      <Upload className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-slate-300">Upload</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="icon-upload-input"
                    />
                  </label>

                  {/* URL Externa */}
                  <div 
                    className="flex items-center justify-center gap-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-emerald-500/50 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('icon-url-input')?.focus()}
                  >
                    <Image className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-slate-300">URL</span>
                  </div>
                </div>

                {/* Input de URL do ícone */}
                <Input
                  id="icon-url-input"
                  value={formData.icon_type === 'url' ? formData.icon_url : ''}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder="https://exemplo.com/icone.png"
                  className="bg-zinc-800 border-zinc-700 text-white mt-2"
                  data-testid="icon-url-field"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Faça upload de uma imagem ou cole a URL de um ícone
                </p>
              </div>

              {/* Telefone do Master */}
              <div>
                <Label htmlFor="master_phone" className="text-slate-300 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  Telefone do Master (WhatsApp)
                </Label>
                <Input
                  id="master_phone"
                  value={formData.master_phone}
                  onChange={(e) => setFormData({ ...formData, master_phone: e.target.value })}
                  placeholder="Ex: 11999999999"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="master-phone-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Digite DDD + número para habilitar o botão WhatsApp
                </p>
              </div>

              {/* Custo por Crédito */}
              <div>
                <Label htmlFor="credit_cost" className="text-slate-300 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  Custo por Crédito (R$)
                </Label>
                <Input
                  id="credit_cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.credit_cost}
                  onChange={(e) => setFormData({ ...formData, credit_cost: e.target.value })}
                  placeholder="Ex: 15.00"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="credit-cost-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Valor que você paga por cada crédito/cliente neste painel
                </p>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCloseDialog}
                  className="text-slate-400 hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-link-btn"
                >
                  {editingLink ? 'Salvar Alterações' : 'Adicionar Link'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
